<template>
  <div class="musubi-page musubi-page--acquisition">
    <header class="musubi-page__head acq-head">
      <div>
        <h1>Acquisition</h1>
        <p class="text-muted">Find, enrich and qualify prospects — warm social listening + cold web/target firms. A prospect becomes a lead once it shows intent.</p>
      </div>
      <a class="btn btn-primary acq-newpipe" href="/acquire/pipeline-wizard">
        <CygnusIcon name="diagram" class="me-1" />New pipeline
      </a>
    </header>

    <div v-if="error" class="alert alert-danger py-2 small">{{ error }}</div>

    <!-- KPIs -->
    <div class="acq-kpis">
      <MusubiKpiCard label="Discovered" :value="counts.discovered" icon="broadcast" />
      <MusubiKpiCard label="Enriched" :value="counts.enriched" :delta="enrichedPct + '% coverage'" icon="bolt" />
      <MusubiKpiCard label="Qualified" :value="counts.qualified" icon="check-circle" />
      <MusubiKpiCard label="Awaiting triage" :value="counts.awaiting" :delta="counts.awaiting ? 'needs review' : 'clear'" :delta-dir="counts.awaiting ? 'down' : 'up'" icon="warning" />
      <MusubiKpiCard label="Promoted" :value="counts.promoted" icon="external-link" />
    </div>

    <!-- Journey ribbon -->
    <MusubiJourneyRibbon v-model="activeStage" :stages="ribbon" class="acq-ribbon" @select="onStage" />

    <div class="acq-grid">
      <!-- Recent leads -->
      <MusubiSectionCard :title="recentTitle" icon="users">
        <template #actions>
          <a class="btn btn-sm btn-link" href="/acquire/prospects">Open full triage →</a>
        </template>
        <div v-if="loading" class="text-muted small py-3">Loading…</div>
        <table v-else-if="recent.length" class="acq-table">
          <thead><tr><th>Lead</th><th>Source</th><th>Score</th><th>Stage</th></tr></thead>
          <tbody>
            <tr v-for="l in recent" :key="l.id">
              <td><div class="acq-lead"><span class="acq-av">{{ initials(l.name) }}</span><div><div class="acq-nm">{{ l.name || '—' }} <span v-if="l.is_warm">🔥</span></div><div class="acq-hl">{{ l.headline || '' }}</div></div></div></td>
              <td class="text-muted small">{{ l.platform }}</td>
              <td><span class="acq-score">{{ l.score }}</span></td>
              <td><MusubiStatusPill :status="l.stage || 'new'" /></td>
            </tr>
          </tbody>
        </table>
        <p v-else class="text-muted small py-3">No leads in this stage.</p>
      </MusubiSectionCard>

      <!-- Quick links / sub-areas -->
      <MusubiSectionCard title="Sources &amp; tools" icon="wrench">
        <a class="acq-link" href="/acquire/sources"><CygnusIcon name="broadcast" /><div><b>Crawl sources</b><span>Social (warm) + web/target (cold)</span></div><CygnusIcon name="chevron-right" class="acq-chev" /></a>
        <a class="acq-link" href="/acquire/signals"><CygnusIcon name="gear" /><div><b>Intent signals</b><span>Keyword / phrase / WARM override</span></div><CygnusIcon name="chevron-right" class="acq-chev" /></a>
        <a class="acq-link" href="/acquire/scout"><CygnusIcon name="bolt" /><div><b>AI Scout</b><span>Evaluate seed firms → cold batch</span></div><CygnusIcon name="chevron-right" class="acq-chev" /></a>
        <a class="acq-link" href="/admin/ai"><CygnusIcon name="bolt" /><div><b>AI enrichment</b><span>Framework · agency · region · address</span></div><CygnusIcon name="chevron-right" class="acq-chev" /></a>
        <a class="acq-link" href="/acquire/prospects"><CygnusIcon name="users" /><div><b>Prospects</b><span>Triage &amp; route (leads = engaged)</span></div><CygnusIcon name="chevron-right" class="acq-chev" /></a>
      </MusubiSectionCard>
    </div>
  </div>
</template>

<script>
/**
 * AdminAcquisition — Part 1 (Acquire) section landing. Composes the design-
 * system primitives (KPI cards, journey ribbon, section cards, status pills,
 * part switcher) over live /acquire/leads data.
 *
 * KPI tiles + ribbon counts come from the tenant-wide aggregate
 * (/admin/leads/stats — the same roll-up the Prospects page uses for its KPI
 * cards), NOT from the recent-leads rows: the leads list endpoint caps a page
 * at up to a few thousand rows, so counting the fetched rows client-side
 * silently truncates once the tenant has more prospects than fit in one page.
 * The "recent leads" table fetches its own small, score-sorted set (top 50)
 * and the ribbon click-filter still works client-side against that set —
 * with only 8 rows ever shown, a 50-row buffer is comfortably enough for the
 * filter to have something to show per stage without a per-click round trip.
 */
import useApi from "@apexl/icarus-core/src/services/api";

export default {
  name: 'AdminAcquisition',
  props: { componentData: { type: Object, default: () => ({}) } },
  data() {
    return {
      api: useApi(),
      rows: [],
      stats: { total: 0, enriched: 0, qualified: 0, promoted: 0, routed: 0, awaiting: 0 },
      loading: false,
      error: null,
      activeStage: null,
    };
  },
  mounted() { this.load(); },
  computed: {
    counts() {
      const s = this.stats;
      return {
        discovered: s.total,
        enriched: s.enriched,
        qualified: s.qualified,
        promoted: s.promoted,
        routed: s.routed,
        awaiting: s.awaiting,
      };
    },
    enrichedPct() {
      return this.counts.discovered ? Math.round((this.counts.enriched / this.counts.discovered) * 100) : 0;
    },
    ribbon() {
      const c = this.counts;
      return [
        { key: 'discovered', label: 'Discovered', count: c.discovered, icon: 'broadcast' },
        { key: 'enriched',   label: 'Enriched',   count: c.enriched,   icon: 'bolt' },
        { key: 'qualified',  label: 'Qualified',  count: c.qualified,  icon: 'check-circle', meta: c.awaiting ? `${c.awaiting} to triage` : '' },
        { key: 'routed',     label: 'Routed',     count: c.routed,     icon: 'diagram' },
        { key: 'promoted',   label: 'Promoted',   count: c.promoted,   icon: 'external-link' },
      ];
    },
    recent() {
      const match = {
        discovered: () => true,
        enriched: (l) => l.enrichment_status === 'found',
        qualified: (l) => l.stage === 'qualified',
        routed: (l) => l.route && l.route !== 'none',
        promoted: (l) => l.stage === 'promoted',
      };
      const f = this.activeStage ? (match[this.activeStage] || (() => true)) : () => true;
      return [...this.rows].filter(f).sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 8);
    },
    recentTitle() {
      const s = this.activeStage;
      return s ? `Leads · ${s}` : 'Top leads by score';
    },
  },
  methods: {
    async load() {
      this.loading = true; this.error = null;
      try {
        const [statsRes, rowsRes] = await Promise.all([
          this.api.get('/api/v1/admin/leads/stats'),
          this.api.get('/api/v1/admin/leads?page=1&per_page=50&sort=score&dir=desc'),
        ]);
        const sp = statsRes?.data ?? statsRes;
        if (sp?.stats) this.stats = sp.stats;
        // Paged body is { data:[rows], total, … }; unwrap an axios-style
        // envelope only when there's no `total` here (mirrors AdminLeads.vue).
        const body = (rowsRes && rowsRes.total !== undefined) ? rowsRes : (rowsRes?.data ?? rowsRes);
        this.rows = body?.data || [];
      } catch (e) {
        this.error = e?.message || 'Failed to load leads.';
      }
      this.loading = false;
    },
    onStage(s) { if (this.activeStage === s.key) this.activeStage = null; },
    initials(name) { return (name || '?').split(/\s+/).map((w) => w[0]).slice(0, 2).join('').toUpperCase(); },
  },
};
</script>

<style scoped>
.acq-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.acq-newpipe { flex: none; white-space: nowrap; }
.acq-kpis { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin: 18px 0; }
.acq-ribbon { margin-bottom: 18px; }
.acq-grid { display: grid; grid-template-columns: 1.6fr 1fr; gap: 16px; }
.acq-table { width: 100%; border-collapse: collapse; }
.acq-table th { text-align: left; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.04em; color: var(--m-text-tertiary); font-weight: 600; padding: 8px 6px; border-bottom: 1px solid var(--m-border); }
.acq-table td { padding: 10px 6px; border-bottom: 1px solid var(--m-border); vertical-align: middle; }
.acq-lead { display: flex; align-items: center; gap: 10px; }
.acq-av { width: 28px; height: 28px; border-radius: 50%; background: var(--m-accent-soft); color: var(--m-accent); display: grid; place-items: center; font-size: 0.7rem; font-weight: 700; }
.acq-nm { font-weight: 650; }
.acq-hl { color: var(--m-text-tertiary); font-size: 0.75rem; }
.acq-score { font-weight: 700; }
.acq-link { display: flex; align-items: center; gap: 12px; padding: 11px 8px; border-bottom: 1px solid var(--m-border); text-decoration: none; color: var(--m-text-primary); }
.acq-link:last-child { border-bottom: none; }
.acq-link:hover { color: var(--m-accent); }
.acq-link > i:first-child { color: var(--m-accent); width: 20px; text-align: center; }
.acq-link b { display: block; font-size: 0.88rem; }
.acq-link span { color: var(--m-text-tertiary); font-size: 0.75rem; }
.acq-chev { margin-left: auto; color: var(--m-text-tertiary); }
@media (max-width: 1024px) { .acq-kpis { grid-template-columns: repeat(2, 1fr); } .acq-grid { grid-template-columns: 1fr; } }
@media (max-width: 640px)  { .acq-kpis { grid-template-columns: 1fr; } }
</style>
